import { Component, OnInit, OnChanges, SimpleChanges, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';

import { fromEvent, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from "rxjs/operators";

import { BaseVraagControlDirective } from '../base/base-vraag-control';
import { Antwoord, Herstelbedrijf, Herstelbedrijven, Plaats, Plaatsnamen, Reparateur, Vraag } from '@models/claims';
import { SharedData } from '@models/shareddata';
import { IBaseVraagboomResultaat, Meetgegevens, Verzekering, VraagboomAntwoord, VraagboomReparateurResultaat } from '@models/vragenboom';
import { DataService } from '../../../services/data.service';
import { MetenMapper } from '../../../mappers/meten.mapper';
import { ClaimService, FormatHelper, RelatieService, SharedDataService } from '@services/public_api';

@Component({
  selector: 'app-radiobutton-list-repair-with-search',
  templateUrl: './radiobutton-list-repair-with-search.component.html',
  styleUrls: ['./radiobutton-list-repair-with-search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RadiobuttonListRepairWithSearchComponent extends BaseVraagControlDirective<VraagboomReparateurResultaat> implements OnInit, OnChanges {
  @ViewChild('PlaatsPostcode', { static: true }) plaatsPostcodeInput: ElementRef;

  selectedRelnr: string;
  selectedRol: string;
  _verzekering: Verzekering;
  _meetgegevens: Meetgegevens;
  resultaten$: Observable<VraagboomAntwoord<IBaseVraagboomResultaat>[]>;
  alleResultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[];
  reparateurs: Reparateur[] = [];
  constanteRelnrEigenReparateur = 'EIGENREPARATEUR';
  plaats: string;
  postcode: string;
  herstellers: Herstelbedrijven;
  plaatsnamen: Plaatsnamen;
  inputPlaats: string;
  isSearching: boolean;
  showSearches: boolean;
  ophalenBezig: boolean;
  herstellersOpgehaald: boolean;
  reparateurUitAntwoord: boolean;
  searchedPlaats: Plaats[];
  showErrorMessage: boolean;
  errorMessage: string;
  errorMessageInit: string = '';
  timeOutIDs: NodeJS.Timeout[] = [];

  constructor(
    private _claimService: ClaimService,
    _dataService: DataService,
    private _relatieService: RelatieService,
    private _sharedDataService: SharedDataService
  ) {
    super(_dataService);
  }

  ngOnInit() {
    this._verzekering = this._dataService.getVerzekering();
    this._meetgegevens = this._dataService.getMeetgegevens();
    this.resultaten$ = this._dataService.resultaten;
    this.resultaten$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        if (res !== undefined && res !== null) {
          this.alleResultaten = res;
        }
      });

    this._sharedDataService.getTeksten().pipe(takeUntil(this.unsubscribe$)).subscribe((sd: SharedData[]) => {
      this.errorMessageInit = sd.find(t => t.Name === 'ophalen_herstellers_mislukt')?.Phrase;
    });

    this._claimService.getPlaatsnamen().pipe(takeUntil(this.unsubscribe$)).subscribe(plaatsen => {
      this.plaatsnamen = plaatsen;
      this.plaatsPostcodeSearch();
    });
  }

  initialize(changes: SimpleChanges): void {
    if (this.antwoordResultaat != null && this.antwoordResultaat.resultaat != null && this.antwoordResultaat.resultaat.postcodeReeks != '' && this.antwoordResultaat.antwoorden != null) {
      this.selectedRelnr = this.antwoordResultaat.resultaat.relatienummer;
      this.selectedRol = this.antwoordResultaat.resultaat.rol;
      this.plaats = this.antwoordResultaat.resultaat.plaatsnaam;
      this.postcode = this.antwoordResultaat.resultaat.postcodeReeks;
      if (this.antwoordResultaat.resultaat.antwoordClaims) {
        this.bepaalReparateursUitAntwoord();
      } else {
        this.herstellersOpgehaald = true;
        this.geefHerstellers(this.postcode);
      }
    } else {
      this.selectedRelnr = '';
      this.selectedRol = '';

      this._relatieService.getRelatie().pipe(takeUntil(this.unsubscribe$)).subscribe(relatie => {
        if (relatie?.relatiegegevens?.length > 0 && relatie.relatiegegevens[0].relatie?.adres?.length > 0) {
          let adres = this._relatieService.getWoonplaats(relatie.relatiegegevens[0].relatie.adres);
          if (adres != null) {
            this.plaats = adres.plaats;
            if (adres.postcode?.length > 1) {
              this.postcode = adres.postcode?.substring(0, 2);
              this.geefHerstellers(adres.postcode);
            } else {
              this.bepaalReparateursUitAntwoord();
            }
          } else {
            this.bepaalReparateursUitAntwoord();
          }
        } else {
          this.bepaalReparateursUitAntwoord();
        }
      }, () => {
        // Error can not result in failure
        this.bepaalReparateursUitAntwoord();
      });
    }
  }

  plaatsPostcodeSearch() {
    // Adding keyup Event Listener on input field
    const search$ = fromEvent(this.plaatsPostcodeInput.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value),
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => this.isSearching = true),
      switchMap((term) => term ? this.getPlaatsPostcode(term) : of<Plaatsnamen>(this.plaatsnamen)),
      tap(() => {
        this.isSearching = false,
          this.showSearches = true;
      }));

    search$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: Plaats[]) => {
        this.isSearching = false
        this.searchedPlaats = data;
      })
  }

  getPlaatsPostcode(term: string): Observable<Plaats[]> {
    if (term?.length > 1) {
      return of(this.filterPlaatsen(term));
    }
    return of(null);
  }

  filterPlaatsen(term: string): Plaats[] {
    if (term && (/^[1-9][0-9]{0,3} ?[a-z]{0,2}$/i.test(term))) {
      // op basis van postcode
      return this.plaatsnamen.plaatsen.filter(p => this.bevatPostcode(p.postcodeReeks, term));
    }
    return this.plaatsnamen.plaatsen.filter(p => this.vervangTerm(p.plaatsnaam).startsWith(this.vervangTerm(term)));
  }

  bevatPostcode(postcodeReeks: string, postcode: string): boolean {
    let postcodeAanwezig = false;

    if (postcode?.length > 0) {
      postcodeReeks.split(',').forEach(pc => {
        if ((postcode.length === 1 && pc.substring(0, 1) === postcode) ||
          (postcode.length > 1 && postcode.substring(0, 2) === pc.substring(0, 2))) {
          postcodeAanwezig = true;
        }
      });
    }
    return postcodeAanwezig;
  }

  vervangTerm(zoek: string): string {
    // spaties en apostrofes uitsluiten
    if (zoek?.length > 0) {
      return zoek.toLowerCase().replace(/[^\w]/g, '');
    }
    return null;
  }

  setPlaats(plaats: Plaats) {
    this.showErrorMessage = false;
    this.plaats = plaats.plaatsnaam;
    this.errorMessage = this.errorMessageInit.replace('[plaatsnaam]', plaats.plaatsnaam);
    this.postcode = plaats.postcodeReeks
    this.geefHerstellers(plaats.postcodeReeks);
    this.showSearches = false;
    // Als alleen de plaatsnaam wijzigt, deze opslaan als relnr nog voorkomt in reparateurs
    if (this.selectedRelnr?.length > 0 && this.reparateurs.filter(r => { r.relnr === this.selectedRelnr })) {
      if (this.selectedRelnr === this.constanteRelnrEigenReparateur) {
        // eigen reparateur gekozen
        this.voegAntwoordToe('', '', 1);
      } else {
        this.voegAntwoordToe(this.selectedRelnr, this.selectedRol, 0);
      }
    }
  }

  geefHerstellers(postcode: string) {
    this.timeOutIDs.forEach(id => clearTimeout(id));
    this.ophalenBezig = true;
    this.reparateurs = [];
    let adres = [];
    let reparateur = new Reparateur();

    const requestSubscription = this._claimService.getHerstellers(this._dataService.getClaimnummer(), postcode.substring(0, 2))
      .subscribe((herstellers: Herstelbedrijven) => {
        this.ophalenBezig = false;
        this.reparateurUitAntwoord = false;
        this.herstellersOpgehaald = true;
        herstellers?.herstelbedrijven?.forEach((hersteller: Herstelbedrijf) => {
          reparateur = new Reparateur();
          reparateur.relnr = hersteller.relatienummer;
          reparateur.naam = hersteller.bedrijfsnaam;
          adres = [];
          adres.push(hersteller.straatOpgemaakt);
          adres.push(hersteller.plaatsOpgemaakt);
          reparateur.adres = FormatHelper.stringMetKommas(adres);
          reparateur.rol = hersteller.rol;
          this.reparateurs.push(reparateur);
        });
        // eigen reparateur
        this.vraag.antwoorden?.forEach((antwoord: Antwoord) => {
          if (!antwoord.soortLijst?.herstelbedrijven) {
            reparateur = new Reparateur();
            reparateur.relnr = this.constanteRelnrEigenReparateur;
            reparateur.naam = antwoord.titel;
            reparateur.adres = antwoord.antwoordregels ? antwoord.antwoordregels[0] : '';
            this.reparateurs.push(reparateur);
          }
        });
      }, () => {
        this.ophalenMislukt();
      });

    // triggering a timeout and capturing the id
    this.timeOutIDs.push(
      setTimeout(() => {
        if (this.ophalenBezig) {
          requestSubscription.unsubscribe();
          this.ophalenMislukt();
        }
      }, 20000)
    );
  }

  ophalenMislukt(): void {
    this.ophalenBezig = false;
    this.bepaalReparateursUitAntwoord();
    this.showErrorMessage = this.herstellersOpgehaald;
  }

  bepaalReparateursUitAntwoord() {
    this.reparateurUitAntwoord = true;
    this.reparateurs = [];

    this.vraag.antwoorden?.forEach((antwoord: Antwoord) => {
      if (antwoord.soortLijst?.herstelbedrijven) {
        let herstellers = antwoord.soortLijst.herstelbedrijven as Herstelbedrijf[];

        herstellers.forEach((hersteller: Herstelbedrijf) => {
          const reparateur = new Reparateur();
          reparateur.relnr = hersteller.relatienummer;
          reparateur.naam = hersteller.bedrijfsnaam;
          reparateur.adres = hersteller.straatOpgemaakt + ' ' + hersteller.plaatsOpgemaakt;
          reparateur.rol = hersteller.rol;
          this.reparateurs.push(reparateur);
        });
      } else {
        const reparateur = new Reparateur();
        reparateur.relnr = this.constanteRelnrEigenReparateur;
        reparateur.naam = antwoord.titel;
        reparateur.adres = antwoord.antwoordregels ? antwoord.antwoordregels[0] : '';
        this.reparateurs.push(reparateur);
      }
    });
  }

  radiobuttonClicked(relnr: string, rol: string): void {
    this.selectedRelnr = relnr;
    this.selectedRol = rol;

    if (this.vraag == null || this.vraag.antwoorden == null) {
      return;
    }
    if (relnr === this.constanteRelnrEigenReparateur) {
      // eigen reparateur gekozen
      this.voegAntwoordToe('', '', 1);
    } else {
      this.voegAntwoordToe(relnr, rol, 0);
    }
  }

  voegAntwoordToe(relnr: string, rol: string, index: number) {
    const antwoorden: Antwoord[] = [];
    if (this.vraag && this.vraag.antwoorden && this.vraag.antwoorden.length > index) {
      antwoorden.push(this.vraag.antwoorden[index]);
    }
    this.setAntwoord(antwoorden, new VraagboomReparateurResultaat(relnr, rol, this.postcode, this.plaats, this.reparateurUitAntwoord));
  }

  geefMeetid(vraag: Vraag, reparateur: Reparateur): string {
    return 'ipoclick-radio-' + this._dataService.getMeetidPazaProces() + '-' +
      this._meetgegevens.status + this._meetgegevens.sleutel +
      MetenMapper.geefVolgnr(this.vraag, this._meetgegevens.reeks) + '_' +
      MetenMapper.format(vraag.titel) + '-' + MetenMapper.format(reparateur.naam);
  }

  validate(vraag: Vraag, antwoorden: Antwoord[], resultaat: VraagboomReparateurResultaat): boolean {
    return true;
  }
}
